import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Fab,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import WorkIcon from "@mui/icons-material/WorkOutlineOutlined";
import { useNavigate } from "react-router-dom";
import { ProjectApi } from "../../../apis";
import { PageHeader, DataView, StatusChip, EmptyState, ConfirmDialog } from "../../../components/ui";
import { useShell } from "../../../components/layout/ShellContext";

const columns = [
  { field: "name", headerName: "Name", sortable: true, primary: true },
  { field: "description", headerName: "Description", sortable: true, secondary: true },
  {
    field: "phases",
    headerName: "Phases",
    sortable: true,
    renderCell: ({ row }) => row.phases?.map((p) => p?.name).join(", ") || "No phases",
  },
  {
    field: "groups",
    headerName: "Groups",
    renderCell: ({ value }) =>
      Array.isArray(value) && value.length
        ? value.map((g) => g?.name || g).join(", ")
        : "—",
  },
  {
    field: "status",
    headerName: "Status",
    sortable: true,
    badge: true,
    renderCell: ({ value }) => <StatusChip status={value} />,
  },
];

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { isMobile } = useShell();

  const fetchProjects = async () => {
    try {
      const res = await ProjectApi.getAllProjects();
      const mapped = res.data.data.projects.map((project) => ({
        ...project,
        groups: project.groups || [],
      }));
      mapped.sort((a, b) => Number(b.id) - Number(a.id));
      setProjects(mapped);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await ProjectApi.deleteProject(pendingDelete.id);
      await fetchProjects();
      setPendingDelete(null);
    } catch (error) {
      console.error("Error deleting project:", error);
    } finally {
      setDeleting(false);
    }
  };

  const visibleProjects = useMemo(() => {
    const term = search.trim().toLowerCase();
    return projects.filter((project) => {
      const matchesStatus =
        statusFilter === "All" || String(project.status).toLowerCase() === statusFilter;
      if (!matchesStatus) return false;
      if (!term) return true;
      return [project.name, project.description]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term));
    });
  }, [projects, statusFilter, search]);

  const addButton = (
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={() => navigate("/admin/projects/add")}
    >
      Add Project
    </Button>
  );

  const rowActions = [
    { label: "Edit", icon: EditIcon, onClick: (row) => navigate(`/admin/projects/${row.id}/edit`) },
    { divider: true },
    { label: "Delete", icon: DeleteIcon, danger: true, onClick: (row) => setPendingDelete(row) },
  ];

  return (
    <Box>
      <PageHeader
        title="Projects"
        subtitle={
          loading ? undefined : `${visibleProjects.length} of ${projects.length} projects`
        }
        actions={!isMobile ? addButton : undefined}
      />

      {/* Filter bar: search grows, status segmented control sits beside it. */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 2,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search projects…"
          fullWidth
          sx={{ maxWidth: { sm: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 19, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          size="small"
          onChange={(_, value) => value && setStatusFilter(value)}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 500,
              px: 2,
              flexGrow: { xs: 1, sm: 0 },
            },
          }}
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="completed">Completed</ToggleButton>
          <ToggleButton value="canceled">Canceled</ToggleButton>
          <ToggleButton value="All">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <DataView
        columns={columns}
        rows={visibleProjects}
        loading={loading}
        actions={rowActions}
        onRowClick={(row) => navigate(`/admin/projects/${row.id}/edit`)}
        empty={
          <EmptyState
            icon={WorkIcon}
            title={search || statusFilter !== "All" ? "No matching projects" : "No projects yet"}
            description={
              search || statusFilter !== "All"
                ? "Try a different search term or status filter."
                : "Create your first project to start tracking time against it."
            }
            action={addButton}
          />
        }
      />

      {/* Mobile gets a floating action button instead of a header button. */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="Add project"
          onClick={() => navigate("/admin/projects/add")}
          sx={{
            position: "fixed",
            right: 16,
            bottom: "calc(76px + env(safe-area-inset-bottom))",
            zIndex: (t) => t.zIndex.appBar - 1,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        busy={deleting}
        title="Delete project?"
        description={
          pendingDelete
            ? `"${pendingDelete.name}" and its association with phases and groups will be removed. This cannot be undone.`
            : undefined
        }
      />
    </Box>
  );
}

export default Projects;
